import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  Building2,
  UserPlus,
  Mail,
  ShoppingCart,
  Package,
  FileText,
  Home,
  Phone,
  Receipt,
  Settings,
  ChevronRight,
  Bell,
  Search,
  Menu,
  X,
  TrendingUp,
  TrendingDown,
  Calendar,
  MoreVertical,
  Plus,
  Filter,
  Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";

// Configurazione moduli Admin
const ADMIN_MODULES = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, color: "text-blue-500" },
  { id: "leads", label: "Leads", icon: UserPlus, color: "text-purple-500", badge: 12 },
  { id: "strutture", label: "Strutture", icon: Building2, color: "text-green-500" },
  { id: "recruiting", label: "Recruiting", icon: Users, color: "text-orange-500", badge: 5 },
  { id: "newsletter", label: "Newsletter", icon: Mail, color: "text-pink-500" },
  { id: "gruppi-acquisto", label: "Gruppi Acquisto", icon: ShoppingCart, color: "text-emerald-500" },
  { id: "ordini", label: "Ordini", icon: Package, color: "text-cyan-500" },
  { id: "fornitori", label: "Fornitori", icon: Package, color: "text-amber-500" },
  { id: "documenti", label: "Documenti", icon: FileText, color: "text-slate-500" },
  { id: "immobiliare", label: "Immobiliare", icon: Home, color: "text-red-500" },
  { id: "telefonia", label: "Telefonia", icon: Phone, color: "text-indigo-500" },
  { id: "fatture", label: "Fatture", icon: Receipt, color: "text-yellow-600" },
  { id: "casadiriposoperte", label: "Casadiriposoperte", icon: Building2, color: "text-violet-500", badge: "App" },
  { id: "impostazioni", label: "Impostazioni", icon: Settings, color: "text-gray-500" },
];

// KPI Demo
const KPI_DATA = [
  { label: "Lead Nuovi", value: "47", change: "+12%", trend: "up", color: "bg-purple-500" },
  { label: "Strutture Attive", value: "156", change: "+3%", trend: "up", color: "bg-green-500" },
  { label: "Ordini Mese", value: "89", change: "+18%", trend: "up", color: "bg-cyan-500" },
  { label: "Fatturato", value: "€24.5k", change: "+8%", trend: "up", color: "bg-yellow-500" },
];

// Attività recenti demo
const RECENT_ACTIVITIES = [
  { type: "lead", text: "Nuovo lead: RSA Villa Serena", time: "2 min fa", color: "bg-purple-100 text-purple-700" },
  { type: "ordine", text: "Ordine #ORD-2024-089 confermato", time: "15 min fa", color: "bg-cyan-100 text-cyan-700" },
  { type: "struttura", text: "Galathea ha aggiornato il profilo", time: "1 ora fa", color: "bg-green-100 text-green-700" },
  { type: "chiamata", text: "Chiamata persa: +39 06 1234567", time: "2 ore fa", color: "bg-red-100 text-red-700" },
  { type: "recruiting", text: "Nuova richiesta OSS - Moretti", time: "3 ore fa", color: "bg-orange-100 text-orange-700" },
];

// Lead demo per pipeline
const LEADS_PIPELINE = {
  nuovo: [
    { id: 1, nome: "RSA Villa Serena", citta: "Roma", valore: "€2.400/mese" },
    { id: 2, nome: "Casa Anziani Gioia", citta: "Milano", valore: "€1.800/mese" },
  ],
  contattato: [
    { id: 3, nome: "Residenza Aurora", citta: "Torino", valore: "€3.200/mese" },
  ],
  qualificato: [
    { id: 4, nome: "Villa dei Pini", citta: "Firenze", valore: "€2.800/mese" },
    { id: 5, nome: "Casa Serena", citta: "Bologna", valore: "€2.100/mese" },
  ],
  proposta: [
    { id: 6, nome: "RSA San Marco", citta: "Venezia", valore: "€4.500/mese" },
  ],
};

export default function AdminDemo() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeModule, setActiveModule] = useState("dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full bg-slate-900 text-white transition-all duration-300
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">A</span>
              </div>
              <span className="font-bold text-lg">Affidya Admin</span>
            </div>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-800 rounded-lg hidden lg:block"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 hover:bg-slate-800 rounded-lg lg:hidden"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-4rem)]">
          {ADMIN_MODULES.map((module) => {
            const Icon = module.icon;
            const isActive = activeModule === module.id;
            return (
              <button
                key={module.id}
                onClick={() => {
                  setActiveModule(module.id);
                  setMobileMenuOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                  ${isActive 
                    ? 'bg-green-500 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                `}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : module.color}`} />
                {sidebarOpen && (
                  <>
                    <span className="flex-1 text-left text-sm font-medium">{module.label}</span>
                    {module.badge && (
                      <Badge className={`${isActive ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'} text-xs`}>
                        {module.badge}
                      </Badge>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-slate-100 rounded-lg lg:hidden"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="relative hidden sm:block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Cerca leads, strutture, ordini..." 
                className="pl-9 w-64 lg:w-80 h-10 bg-slate-50 border-slate-200"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 hover:bg-slate-100 rounded-lg">
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">SA</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-slate-900">Super Admin</p>
                <p className="text-xs text-slate-500">admin@affidya.it</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {activeModule === "dashboard" && (
            <div className="space-y-6">
              {/* Page Title */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
                  <p className="text-slate-500">Panoramica generale del sistema</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    Oggi
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Esporta
                  </Button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {KPI_DATA.map((kpi, index) => (
                  <Card key={index} className="border-0 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">{kpi.label}</p>
                          <p className="text-2xl font-bold text-slate-900">{kpi.value}</p>
                        </div>
                        <div className={`w-12 h-12 ${kpi.color} rounded-xl flex items-center justify-center`}>
                          {kpi.trend === "up" ? (
                            <TrendingUp className="w-6 h-6 text-white" />
                          ) : (
                            <TrendingDown className="w-6 h-6 text-white" />
                          )}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-1">
                        <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                          {kpi.change}
                        </span>
                        <span className="text-sm text-slate-400">vs mese scorso</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Activities */}
                <Card className="border-0 shadow-sm lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      Attività Recenti
                      <Button variant="ghost" size="sm">Vedi tutte</Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {RECENT_ACTIVITIES.map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`px-2 py-1 rounded text-xs font-medium ${activity.color}`}>
                          {activity.type}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 truncate">{activity.text}</p>
                          <p className="text-xs text-slate-400">{activity.time}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Pipeline Preview */}
                <Card className="border-0 shadow-sm lg:col-span-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center justify-between">
                      Pipeline Leads
                      <Button variant="ghost" size="sm">
                        Vai a Leads <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-3">
                      {Object.entries(LEADS_PIPELINE).map(([stage, leads]) => (
                        <div key={stage} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-slate-500 uppercase">{stage}</span>
                            <Badge variant="secondary" className="text-xs">{leads.length}</Badge>
                          </div>
                          <div className="space-y-2">
                            {leads.slice(0, 2).map((lead) => (
                              <div 
                                key={lead.id}
                                className="p-3 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                              >
                                <p className="text-sm font-medium text-slate-900 truncate">{lead.nome}</p>
                                <p className="text-xs text-slate-500">{lead.citta}</p>
                                <p className="text-xs font-medium text-green-600 mt-1">{lead.valore}</p>
                              </div>
                            ))}
                            {leads.length > 2 && (
                              <p className="text-xs text-center text-slate-400">+{leads.length - 2} altri</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeModule === "leads" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Leads</h1>
                  <p className="text-slate-500">Gestisci la pipeline commerciale</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filtri
                  </Button>
                  <Button size="sm" className="bg-purple-500 hover:bg-purple-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Lead
                  </Button>
                </div>
              </div>

              {/* Kanban Board */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { stage: "Nuovo", color: "bg-slate-500", leads: LEADS_PIPELINE.nuovo },
                  { stage: "Contattato", color: "bg-blue-500", leads: LEADS_PIPELINE.contattato },
                  { stage: "Qualificato", color: "bg-yellow-500", leads: LEADS_PIPELINE.qualificato },
                  { stage: "Proposta", color: "bg-green-500", leads: LEADS_PIPELINE.proposta },
                ].map(({ stage, color, leads }) => (
                  <div key={stage} className="bg-slate-100 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 ${color} rounded-full`} />
                        <span className="font-semibold text-slate-700">{stage}</span>
                      </div>
                      <Badge variant="secondary">{leads.length}</Badge>
                    </div>
                    <div className="space-y-2">
                      {leads.map((lead) => (
                        <Card key={lead.id} className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{lead.nome}</p>
                                <p className="text-sm text-slate-500">{lead.citta}</p>
                              </div>
                              <button className="p-1 hover:bg-slate-100 rounded">
                                <MoreVertical className="w-4 h-4 text-slate-400" />
                              </button>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <span className="text-sm font-medium text-green-600">{lead.valore}</span>
                              <div className="w-6 h-6 bg-slate-200 rounded-full" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      <button className="w-full p-2 border-2 border-dashed border-slate-300 rounded-lg text-slate-400 hover:border-slate-400 hover:text-slate-500 transition-colors">
                        <Plus className="w-4 h-4 mx-auto" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeModule === "strutture" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Strutture</h1>
                  <p className="text-slate-500">156 strutture registrate</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-2" />
                    Filtri
                  </Button>
                  <Button size="sm" className="bg-green-500 hover:bg-green-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuova Struttura
                  </Button>
                </div>
              </div>

              {/* Table */}
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Struttura</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Città</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Tipologia</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Posti</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Stato</th>
                          <th className="text-left p-4 text-sm font-medium text-slate-500">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[
                          { nome: "Residenza Galathea", citta: "Roma", tipo: "RSA", posti: 80, stato: "Attiva" },
                          { nome: "Villa Serena", citta: "Milano", tipo: "Casa Riposo", posti: 45, stato: "Attiva" },
                          { nome: "Casa Anziani Aurora", citta: "Torino", tipo: "RSA", posti: 120, stato: "Attiva" },
                          { nome: "Residenza dei Pini", citta: "Firenze", tipo: "RA", posti: 60, stato: "In attesa" },
                        ].map((s, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                  <Building2 className="w-5 h-5 text-green-600" />
                                </div>
                                <span className="font-medium text-slate-900">{s.nome}</span>
                              </div>
                            </td>
                            <td className="p-4 text-slate-600">{s.citta}</td>
                            <td className="p-4 text-slate-600">{s.tipo}</td>
                            <td className="p-4 text-slate-600">{s.posti}</td>
                            <td className="p-4">
                              <Badge className={s.stato === "Attiva" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                                {s.stato}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Placeholder per altri moduli */}
          {!["dashboard", "leads", "strutture"].includes(activeModule) && (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {(() => {
                    const module = ADMIN_MODULES.find(m => m.id === activeModule);
                    const Icon = module?.icon || Settings;
                    return <Icon className={`w-8 h-8 ${module?.color || 'text-slate-400'}`} />;
                  })()}
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">
                  Modulo {ADMIN_MODULES.find(m => m.id === activeModule)?.label}
                </h2>
                <p className="text-slate-500 mb-4">
                  Questo modulo sarà sviluppato nella fase dedicata
                </p>
                <Badge variant="secondary">Coming Soon</Badge>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
